package structs.account

import kotlinx.serialization.Serializable

/**
 * Billing and subscription status for a player's account.
 *
 * The cycle window is a calendar month anchored to [cycleStartMillis].
 * Token accounting is the source of truth — [remainingInferenceMinutes] is
 * a derived view rendered for the legacy dashboard only and is NOT serialized.
 *
 * @param credits Current credit balance expressed at 100 credits = $1.00
 *   of operator-paid inference. Derived from per-model USD via
 *   [accounting.ModelPricing.usdToCredits] at flush time. Used for
 *   overage purchases and ad-hoc top-ups outside the subscription cap.
 *   Floor at 0.0 (see [accounting.BillingSync.flushTurnUsageForUser]).
 * @param plan The subscription plan tier.
 * @param tokensCapPerCycle Monthly token budget the plan grants. Source of
 *   truth for cap enforcement. Mirrors [AccountPlan.tokenCapPerCycle] at
 *   cycle start.
 * @param tokensUsedThisCycle Running total of tokens consumed in the current
 *   cycle. Incremented by `flushTurnUsage` after every turn.
 * @param cycleStartMillis Epoch millis at which the current cycle began.
 * @param cycleEndMillis Epoch millis at which the current cycle ends and the
 *   next cycle is scheduled. 0 means no cycle is scheduled yet.
 * @param planResetDate Legacy epoch-millis field for the cycle end. Kept in
 *   sync with [cycleEndMillis] at write time for backwards-compat reads on
 *   the cloud-save record (the dashboard's UsageOverlay reads this field).
 * @param autoRenew Whether the plan auto-renews at the end of the cycle.
 *
 * Derived view (not serialized):
 * @property remainingInferenceMinutes Tokens used so far in minutes, at the
 *   documented throughput assumption ([tokensPerMinuteAssumption]). Renders
 *   in the dashboard as "minutes used". Floor at 0 once cap is reached.
 */
@Serializable
data class BillingStatus(
    var credits: Double = 0.0,
    var plan: AccountPlan = AccountPlan.FREE,
    var tokensCapPerCycle: Long = 0L,
    var tokensUsedThisCycle: Long = 0L,
    var cycleStartMillis: Long = 0L,
    var cycleEndMillis: Long = 0L,
    var planResetDate: Long = 0L,
    var autoRenew: Boolean = false,
    /**
     * Additive subscription add-on rows. Each row grants a tier-
     * month (or modes) for a fixed window, summing into the
     * effective cap, modes, and concurrent-game limits. The
     * player's base [plan] is never modified by promo redemption;
     * add-ons are additive and time-bounded.
     *
     * @see SubscriptionAddOn
     * @see effectiveSubscription
     */
    var addOns: List<SubscriptionAddOn> = emptyList(),
    /**
     * Whether promo-code grants increment `BillingStatus.credits`.
     *
     * Operator policy per Phase-2 (2026-08-20): only paid plans
     * grant credits; promo add-ons grant subscriptions, not
     * credits. Defaults to `false`. Flipping this to `true`
     * requires an operator config change in `autogenesis-conf`
     * plus a code review of [PromoEffectApplier.applyGrantedItems]'
     * credit path; both paths are gated by this flag.
     *
     * @see structs.accelbyte.promo.PromoOutcomeType.CREDITS_GRANT
     */
    var promoCreditsEnabled: Boolean = false,
)
{
    /**
     * Length of a billing cycle in milliseconds (30 days). Public so the
     * cycle-reset helper in [AccountSettingsLookup] can advance windows.
     */
    val cycleMillis: Long get() = 30L * 24L * 60L * 60L * 1000L

    /** Tokens-per-minute throughput used for the derived minutes view. */
    val tokensPerMinuteAssumption: Long get() = 1500L

    /**
     * Effective end of the current cycle. Reads [cycleEndMillis] when set;
     * falls back to [cycleStartMillis] + [cycleMillis] when the cycle start
     * was provided but the end was not (the common case when the cycle is
     * first created). Returns 0 when neither is set.
     */
    val effectiveCycleEndMillis: Long
        get() = if (cycleEndMillis > 0L) cycleEndMillis
                else if (cycleStartMillis > 0L) cycleStartMillis + cycleMillis
                else 0L

    /**
     * Derived minutes remaining in the cycle, rounded down. Returns 0 when
     * [tokensUsedThisCycle] is already at or above [tokensCapPerCycle], or
     * when no cap is configured.
     */
    val remainingInferenceMinutes: Long
        get() = if (tokensCapPerCycle <= 0L || tokensUsedThisCycle >= tokensCapPerCycle) 0L
                else ((tokensCapPerCycle - tokensUsedThisCycle) / tokensPerMinuteAssumption)
}

/**
 * Core account data for a player, combining identity and billing information.
 * Saved to AccelByte cloud save as an admin-only record accessible only by
 * server-extend and the main server module.
 *
 * @param accelByteUserId The player's AccelByte user ID
 * @param displayName The player's display name
 * @param billingStatus The player's billing and subscription status
 * @param bringYourOwnApiKey Whether the player has supplied their own LLM API key
 *                           (BYO mode). When true, the matchmaking algorithm treats
 *                           the player as the cheapest possible cost class because
 *                           the operator does not pay for their inference.
 * @param byoKeyItemId Optional analytic tag describing the BYO key (provider + last
 *                     four chars, etc.). Not used by the algorithm; useful for
 *                     logging and operator dashboards.
 * @see BillingStatus
 * @see AccountPlan
 */
@Serializable
data class AccountSettings(
    var accelByteUserId: String = "",
    var displayName: String = "",
    var billingStatus: BillingStatus = BillingStatus(),
    var bringYourOwnApiKey: Boolean = false,
    var byoKeyItemId: String = "",
    /** Durable promo operation receipts used to make reward application idempotent. */
    var promoReceipts: List<PromoRedemptionReceipt> = emptyList()
)

@Serializable
data class PromoRedemptionReceipt(
    val operationId: String,
    val outcomeType: String,
    val amount: Double,
    val plan: AccountPlan? = null,
    val extraSubscriptionDays: Int? = null,
    val appliedAtMillis: Long = 0L,
)
